'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { ShoppingCart, History, User, Layers, Box, AlertTriangle, Download, ChevronRight, Search, X, Check, Clock, CheckCircle2, AlertCircle, TrendingUp, Lightbulb, RefreshCw, ChevronLeft, Package, Truck, ClipboardList } from 'lucide-react';

// ─── Mother Cube Data (from cube-master) ─────────────────────────────────────
interface MotherCubeVersion {
  version: string;
  date: string;
  boxes: BoxDef[];
}

interface BoxDef {
  boxCode: string;
  boxName: string;
  kits: KitDef[];
}

interface KitDef {
  kitCode: string;
  kitName: string;
  qty: number;
  components: ComponentDef[];
}

interface ComponentDef {
  itemCode: string;
  itemName: string;
  requiredQtyPerKit: number;
}

interface MotherCube {
  id: string;
  motherCubeNo: string;
  motherCubeName: string;
  versions: MotherCubeVersion[];
  status: 'Active' | 'Inactive';
}

// ─── Item Inventory ───────────────────────────────────────────────────────────
interface ItemStock {
  itemCode: string;
  itemName: string;
  availableQty: number;
}

const ITEM_INVENTORY: ItemStock[] = [
  { itemCode: 'ITM-001', itemName: 'Paracetamol 500mg', availableQty: 120 },
  { itemCode: 'ITM-002', itemName: 'Antiseptic Wipes (Pack)', availableQty: 45 },
  { itemCode: 'ITM-003', itemName: 'Sterile Gauze Pad', availableQty: 200 },
  { itemCode: 'ITM-004', itemName: 'Medical Tape Roll', availableQty: 30 },
  { itemCode: 'ITM-005', itemName: 'Surgical Gloves (Pair)', availableQty: 90 },
  { itemCode: 'ITM-006', itemName: 'Scalpel Blade No.22', availableQty: 40 },
  { itemCode: 'ITM-007', itemName: 'Suture Kit', availableQty: 18 },
  { itemCode: 'ITM-008', itemName: 'IV Cannula 18G', availableQty: 0 },
  { itemCode: 'ITM-009', itemName: 'IV Drip Set', availableQty: 12 },
  { itemCode: 'ITM-010', itemName: 'Alcohol Swab (Pack)', availableQty: 55 },
  { itemCode: 'ITM-011', itemName: 'Oxygen Mask', availableQty: 0 },
  { itemCode: 'ITM-012', itemName: 'Nasal Cannula', availableQty: 25 },
  { itemCode: 'ITM-013', itemName: 'Wound Dressing Large', availableQty: 60 },
  { itemCode: 'ITM-014', itemName: 'Trauma Bandage', availableQty: 8 },
  { itemCode: 'ITM-015', itemName: 'Saline Solution 500ml', availableQty: 35 },
];

const MOTHER_CUBES: MotherCube[] = [
  {
    id: '1', motherCubeNo: 'MC-001', motherCubeName: 'Primary Medical Cube', status: 'Active',
    versions: [
      {
        version: 'v2.0', date: '12/29/2025',
        boxes: [
          {
            boxCode: 'BOX-001', boxName: 'Oral Medication Box',
            kits: [
              {
                kitCode: 'KIT-001', kitName: 'Basic First Aid Kit', qty: 2,
                components: [
                  { itemCode: 'ITM-001', itemName: 'Paracetamol 500mg', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-002', itemName: 'Antiseptic Wipes (Pack)', requiredQtyPerKit: 1 },
                  { itemCode: 'ITM-003', itemName: 'Sterile Gauze Pad', requiredQtyPerKit: 4 },
                  { itemCode: 'ITM-004', itemName: 'Medical Tape Roll', requiredQtyPerKit: 1 },
                ],
              },
            ],
          },
          {
            boxCode: 'BOX-002', boxName: 'Wound Care Box',
            kits: [
              {
                kitCode: 'KIT-002', kitName: 'Wound Care Kit', qty: 1,
                components: [
                  { itemCode: 'ITM-013', itemName: 'Wound Dressing Large', requiredQtyPerKit: 3 },
                  { itemCode: 'ITM-014', itemName: 'Trauma Bandage', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-003', itemName: 'Sterile Gauze Pad', requiredQtyPerKit: 5 },
                ],
              },
            ],
          },
        ],
      },
      {
        version: 'v1.0', date: '12/23/2025',
        boxes: [
          {
            boxCode: 'BOX-001', boxName: 'Oral Medication Box',
            kits: [
              {
                kitCode: 'KIT-001', kitName: 'Basic First Aid Kit', qty: 1,
                components: [
                  { itemCode: 'ITM-001', itemName: 'Paracetamol 500mg', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-002', itemName: 'Antiseptic Wipes (Pack)', requiredQtyPerKit: 1 },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: '2', motherCubeNo: 'MC-002', motherCubeName: 'Trauma Response Cube', status: 'Active',
    versions: [
      {
        version: 'v2.0', date: '12/29/2025',
        boxes: [
          {
            boxCode: 'BOX-003', boxName: 'Trauma Box',
            kits: [
              {
                kitCode: 'KIT-003', kitName: 'Trauma Response Kit', qty: 3,
                components: [
                  { itemCode: 'ITM-005', itemName: 'Surgical Gloves (Pair)', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-006', itemName: 'Scalpel Blade No.22', requiredQtyPerKit: 1 },
                  { itemCode: 'ITM-007', itemName: 'Suture Kit', requiredQtyPerKit: 1 },
                  { itemCode: 'ITM-014', itemName: 'Trauma Bandage', requiredQtyPerKit: 3 },
                ],
              },
            ],
          },
        ],
      },
      {
        version: 'v1.0', date: '12/23/2025',
        boxes: [
          {
            boxCode: 'BOX-003', boxName: 'Trauma Box',
            kits: [
              {
                kitCode: 'KIT-003', kitName: 'Trauma Response Kit', qty: 2,
                components: [
                  { itemCode: 'ITM-005', itemName: 'Surgical Gloves (Pair)', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-014', itemName: 'Trauma Bandage', requiredQtyPerKit: 2 },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: '3', motherCubeNo: 'MC-003', motherCubeName: 'Surgical Support Cube', status: 'Active',
    versions: [
      {
        version: 'v2.0', date: '12/29/2025',
        boxes: [
          {
            boxCode: 'BOX-004', boxName: 'Surgical Box',
            kits: [
              {
                kitCode: 'KIT-004', kitName: 'Surgical Procedure Kit', qty: 2,
                components: [
                  { itemCode: 'ITM-005', itemName: 'Surgical Gloves (Pair)', requiredQtyPerKit: 3 },
                  { itemCode: 'ITM-006', itemName: 'Scalpel Blade No.22', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-008', itemName: 'IV Cannula 18G', requiredQtyPerKit: 1 },
                  { itemCode: 'ITM-009', itemName: 'IV Drip Set', requiredQtyPerKit: 1 },
                ],
              },
            ],
          },
        ],
      },
      {
        version: 'v1.0', date: '12/25/2025',
        boxes: [
          {
            boxCode: 'BOX-004', boxName: 'Surgical Box',
            kits: [
              {
                kitCode: 'KIT-004', kitName: 'Surgical Procedure Kit', qty: 1,
                components: [
                  { itemCode: 'ITM-005', itemName: 'Surgical Gloves (Pair)', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-006', itemName: 'Scalpel Blade No.22', requiredQtyPerKit: 1 },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: '4', motherCubeNo: 'MC-004', motherCubeName: 'IV & Oxygen Cube', status: 'Active',
    versions: [
      {
        version: 'v2.0', date: '12/29/2025',
        boxes: [
          {
            boxCode: 'BOX-005', boxName: 'IV Therapy Box',
            kits: [
              {
                kitCode: 'KIT-005', kitName: 'IV Therapy Kit', qty: 2,
                components: [
                  { itemCode: 'ITM-008', itemName: 'IV Cannula 18G', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-009', itemName: 'IV Drip Set', requiredQtyPerKit: 1 },
                  { itemCode: 'ITM-015', itemName: 'Saline Solution 500ml', requiredQtyPerKit: 2 },
                  { itemCode: 'ITM-011', itemName: 'Oxygen Mask', requiredQtyPerKit: 1 },
                  { itemCode: 'ITM-012', itemName: 'Nasal Cannula', requiredQtyPerKit: 1 },
                ],
              },
            ],
          },
        ],
      },
      {
        version: 'v1.0', date: '12/23/2025',
        boxes: [
          {
            boxCode: 'BOX-005', boxName: 'IV Therapy Box',
            kits: [
              {
                kitCode: 'KIT-005', kitName: 'IV Therapy Kit', qty: 1,
                components: [
                  { itemCode: 'ITM-008', itemName: 'IV Cannula 18G', requiredQtyPerKit: 1 },
                  { itemCode: 'ITM-009', itemName: 'IV Drip Set', requiredQtyPerKit: 1 },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

// ─── Order History Data ───────────────────────────────────────────────────────
type OrderStatus = 'Pending' | 'In Production' | 'Fulfilled' | 'Partial' | 'Cancelled';

// ─── Fulfillment Stage ────────────────────────────────────────────────────────
type FulfillmentStage = 'Order' | 'Picking' | 'Dispatch';

interface StageInfo {
  stage: FulfillmentStage;
  completedAt?: string;
  note?: string;
}

interface OrderRecord {
  id: string;
  orderNo: string;
  customerName: string;
  deliveryDate: string;
  description: string;
  motherCubes: string[];
  totalBoxes: number;
  totalKits: number;
  totalItems: number;
  fulfillmentPct: number;
  status: OrderStatus;
  createdDate: string;
  createdBy: string;
  stages: StageInfo[];
  currentStage: FulfillmentStage;
}

const INITIAL_ORDERS: OrderRecord[] = [
  { id: '1', orderNo: 'ORD-20260710-001', customerName: 'BHISHM', deliveryDate: '25 Jul 2026', description: 'Emergency medical supplies', motherCubes: ['MC-001 v2.0', 'MC-002 v2.0'], totalBoxes: 4, totalKits: 8, totalItems: 45, fulfillmentPct: 100, status: 'Fulfilled', createdDate: '10 Jul 2026', createdBy: 'Prasun Sharma', currentStage: 'Dispatch', stages: [{ stage: 'Order', completedAt: '10 Jul 2026', note: 'Order placed' }, { stage: 'Picking', completedAt: '18 Jul 2026', note: 'All items picked' }, { stage: 'Dispatch', completedAt: '25 Jul 2026', note: 'Dispatched to BHISHM' }] },
  { id: '2', orderNo: 'ORD-20260715-002', customerName: 'ARJUN MEDICAL', deliveryDate: '30 Jul 2026', description: 'Surgical kit replenishment', motherCubes: ['MC-003 v2.0'], totalBoxes: 2, totalKits: 4, totalItems: 28, fulfillmentPct: 60, status: 'Partial', createdDate: '15 Jul 2026', createdBy: 'Prasun Sharma', currentStage: 'Picking', stages: [{ stage: 'Order', completedAt: '15 Jul 2026', note: 'Order placed' }, { stage: 'Picking', note: 'Picking in progress — 60% complete' }] },
  { id: '3', orderNo: 'ORD-20260720-003', customerName: 'DRONA HEALTH', deliveryDate: '05 Aug 2026', description: 'IV therapy kits', motherCubes: ['MC-004 v1.0'], totalBoxes: 2, totalKits: 2, totalItems: 10, fulfillmentPct: 0, status: 'In Production', createdDate: '20 Jul 2026', createdBy: 'Prasun Sharma', currentStage: 'Order', stages: [{ stage: 'Order', completedAt: '20 Jul 2026', note: 'Order placed — awaiting kitting' }] },
  { id: '4', orderNo: 'ORD-20260722-004', customerName: 'KARNA PHARMA', deliveryDate: '10 Aug 2026', description: 'Trauma response kits', motherCubes: ['MC-002 v1.0', 'MC-003 v1.0'], totalBoxes: 3, totalKits: 6, totalItems: 32, fulfillmentPct: 0, status: 'Pending', createdDate: '22 Jul 2026', createdBy: 'Prasun Sharma', currentStage: 'Order', stages: [{ stage: 'Order', note: 'Order pending — not yet started' }] },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
interface SelectedVersion {
  cubeId: string;
  version: string;
}

interface FulfillmentResult {
  totalBoxes: number;
  totalKits: number;
  totalItems: number;
  canFulfill: boolean;
  productionSuggestion: number;
  shortageItems: ShortageItem[];
  requisitions: RequisitionSuggestion[];
  itemBreakdown: ItemBreakdownRow[];
}

interface ShortageItem {
  itemCode: string;
  itemName: string;
  required: number;
  available: number;
  shortage: number;
}

interface RequisitionSuggestion {
  kitCode: string;
  kitName: string;
  requiredQty: number;
  canMake: number;
  shortfall: number;
}

interface ItemBreakdownRow {
  itemCode: string;
  itemName: string;
  required: number;
  available: number;
  sufficient: boolean;
}

function computeFulfillment(selectedVersions: SelectedVersion[]): FulfillmentResult {
  // Aggregate all items needed across selected cube versions
  const itemRequirements: Record<string, { itemName: string; required: number }> = {};
  const kitRequirements: Record<string, { kitName: string; required: number }> = {};
  let totalBoxes = 0;
  let totalKits = 0;

  for (const sel of selectedVersions) {
    const cube = MOTHER_CUBES.find((c) => c.id === sel.cubeId);
    if (!cube) continue;
    const versionData = cube.versions.find((v) => v.version === sel.version);
    if (!versionData) continue;

    for (const box of versionData.boxes) {
      totalBoxes++;
      for (const kit of box.kits) {
        totalKits += kit.qty;
        const kitKey = kit.kitCode;
        if (!kitRequirements[kitKey]) kitRequirements[kitKey] = { kitName: kit.kitName, required: 0 };
        kitRequirements[kitKey].required += kit.qty;

        for (const comp of kit.components) {
          const totalNeeded = comp.requiredQtyPerKit * kit.qty;
          if (!itemRequirements[comp.itemCode]) {
            itemRequirements[comp.itemCode] = { itemName: comp.itemName, required: 0 };
          }
          itemRequirements[comp.itemCode].required += totalNeeded;
        }
      }
    }
  }

  const totalItems = Object.values(itemRequirements).reduce((s, v) => s + v.required, 0);

  // Check availability
  const shortageItems: ShortageItem[] = [];
  const itemBreakdown: ItemBreakdownRow[] = [];
  let canFulfill = true;

  for (const [code, req] of Object.entries(itemRequirements)) {
    const stock = ITEM_INVENTORY.find((i) => i.itemCode === code);
    const available = stock?.availableQty ?? 0;
    const sufficient = available >= req.required;
    if (!sufficient) {
      canFulfill = false;
      shortageItems.push({
        itemCode: code,
        itemName: req.itemName,
        required: req.required,
        available,
        shortage: req.required - available,
      });
    }
    itemBreakdown.push({ itemCode: code, itemName: req.itemName, required: req.required, available, sufficient });
  }

  // Production suggestion: how many full sets can be made
  let productionSuggestion = 0;
  if (Object.keys(itemRequirements).length > 0) {
    const ratios = Object.entries(itemRequirements).map(([code, req]) => {
      const stock = ITEM_INVENTORY.find((i) => i.itemCode === code);
      const available = stock?.availableQty ?? 0;
      return req.required > 0 ? available / req.required : Infinity;
    });
    productionSuggestion = Math.floor(Math.min(...ratios));
  }

  // Requisition suggestions per kit
  const requisitions: RequisitionSuggestion[] = Object.entries(kitRequirements).map(([code, kit]) => {
    // Find components for this kit
    let minCanMake = Infinity;
    for (const cube of MOTHER_CUBES) {
      for (const ver of cube.versions) {
        for (const box of ver.boxes) {
          const kitDef = box.kits.find((k) => k.kitCode === code);
          if (kitDef) {
            for (const comp of kitDef.components) {
              const stock = ITEM_INVENTORY.find((i) => i.itemCode === comp.itemCode);
              const available = stock?.availableQty ?? 0;
              const canMakeFromThis = comp.requiredQtyPerKit > 0 ? Math.floor(available / comp.requiredQtyPerKit) : Infinity;
              minCanMake = Math.min(minCanMake, canMakeFromThis);
            }
          }
        }
      }
    }
    if (minCanMake === Infinity) minCanMake = kit.required;
    const canMake = Math.min(minCanMake, kit.required);
    return {
      kitCode: code,
      kitName: kit.kitName,
      requiredQty: kit.required,
      canMake,
      shortfall: Math.max(0, kit.required - canMake),
    };
  });

  return { totalBoxes, totalKits, totalItems, canFulfill, productionSuggestion, shortageItems, requisitions, itemBreakdown };
}

const orderStatusColor = (status: OrderStatus) => {
  if (status === 'Fulfilled') return 'text-emerald-600 bg-emerald-50 border border-emerald-200';
  if (status === 'In Production') return 'text-blue-600 bg-blue-50 border border-blue-200';
  if (status === 'Pending') return 'text-amber-600 bg-amber-50 border border-amber-200';
  if (status === 'Partial') return 'text-orange-600 bg-orange-50 border border-orange-200';
  return 'text-gray-500 bg-gray-50 border border-gray-200';
};

const orderStatusIcon = (status: OrderStatus) => {
  if (status === 'Fulfilled') return <CheckCircle2 size={13} className="text-emerald-600" />;
  if (status === 'In Production') return <Clock size={13} className="text-blue-600" />;
  if (status === 'Pending') return <AlertCircle size={13} className="text-amber-600" />;
  if (status === 'Partial') return <AlertTriangle size={13} className="text-orange-600" />;
  return <X size={13} className="text-gray-500" />;
};

// ─── CSV Download ─────────────────────────────────────────────────────────────
function downloadShortageCSV(items: ShortageItem[], orderNo: string) {
  const header = ['Item Code', 'Item Name', 'Required Qty', 'Available Qty', 'Shortage'];
  const rows = items.map((i) => [i.itemCode, i.itemName, i.required, i.available, i.shortage]);
  const csvContent = [header, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `shortage-list-${orderNo || 'draft'}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ─── Place Order Tab ──────────────────────────────────────────────────────────
function PlaceOrderTab({ onOrderCreated }: { onOrderCreated: (order: OrderRecord) => void }) {
  const [customerName, setCustomerName] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [description, setDescription] = useState('');
  const [selectedVersions, setSelectedVersions] = useState<SelectedVersion[]>([]);
  const [fulfillment, setFulfillment] = useState<FulfillmentResult | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedBoxes, setExpandedBoxes] = useState<Set<string>>(new Set());

  const toggleVersion = (cubeId: string, version: string) => {
    setSelectedVersions((prev) => {
      const exists = prev.find((s) => s.cubeId === cubeId && s.version === version);
      if (exists) return prev.filter((s) => !(s.cubeId === cubeId && s.version === version));
      return [...prev, { cubeId, version }];
    });
  };

  const isSelected = (cubeId: string, version: string) =>
    selectedVersions.some((s) => s.cubeId === cubeId && s.version === version);

  useEffect(() => {
    if (selectedVersions.length > 0) {
      setFulfillment(computeFulfillment(selectedVersions));
    } else {
      setFulfillment(null);
    }
  }, [selectedVersions]);

  const toggleBox = (key: string) => {
    setExpandedBoxes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleReset = () => {
    setCustomerName('');
    setDeliveryDate('');
    setDescription('');
    setSelectedVersions([]);
    setFulfillment(null);
    setExpandedBoxes(new Set());
  };

  const handleCreateOrder = () => {
    if (!customerName || !deliveryDate || selectedVersions.length === 0) return;
    const f = fulfillment ?? computeFulfillment(selectedVersions);
    const orderNo = `ORD-${Date.now().toString().slice(-8)}`;
    const newOrder: OrderRecord = {
      id: Date.now().toString(),
      orderNo,
      customerName,
      deliveryDate,
      description,
      motherCubes: selectedVersions.map((s) => {
        const cube = MOTHER_CUBES.find((c) => c.id === s.cubeId);
        return `${cube?.motherCubeNo} ${s.version}`;
      }),
      totalBoxes: f.totalBoxes,
      totalKits: f.totalKits,
      totalItems: f.totalItems,
      fulfillmentPct: f.canFulfill ? 100 : Math.round((f.productionSuggestion / Math.max(1, f.totalKits)) * 100),
      status: f.canFulfill ? 'Pending' : 'Partial',
      createdDate: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      createdBy: 'Prasun Sharma',
      currentStage: 'Order' as FulfillmentStage,
      stages: [{ stage: 'Order' as FulfillmentStage, completedAt: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }), note: 'Order placed' }],
    };
    onOrderCreated(newOrder);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      handleReset();
    }, 2500);
  };

  // Collect boxes from selected versions
  const selectedBoxes: { cubeId: string; cubeName: string; version: string; box: BoxDef }[] = [];
  for (const sel of selectedVersions) {
    const cube = MOTHER_CUBES.find((c) => c.id === sel.cubeId);
    if (!cube) continue;
    const versionData = cube.versions.find((v) => v.version === sel.version);
    if (!versionData) continue;
    for (const box of versionData.boxes) {
      selectedBoxes.push({ cubeId: sel.cubeId, cubeName: cube.motherCubeName, version: sel.version, box });
    }
  }

  const canSubmit = customerName.trim() && deliveryDate && selectedVersions.length > 0;

  return (
    <div className="flex gap-5 h-full">
      {/* Left Panel */}
      <div className="w-80 flex-shrink-0 flex flex-col gap-4">
        {/* Customer Card */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
            <User size={16} className="text-primary" />
            <span className="font-semibold text-sm text-foreground">Customer</span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter customer name"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                Delivery Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Order description"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>
        </div>

        {/* Mother Cubes Card */}
        <div className="bg-card border border-border rounded-xl p-4 flex-1 flex flex-col min-h-0">
          <div className="flex items-center gap-2 mb-1 pb-3 border-b border-border">
            <Layers size={16} className="text-primary" />
            <span className="font-semibold text-sm text-foreground">Mother Cubes</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Select specific versions to populate boxes</p>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {MOTHER_CUBES.filter((c) => c.status === 'Active').map((cube) => (
              <div key={cube.id} className="border border-border rounded-lg p-3 bg-background">
                <p className="text-xs font-semibold text-foreground mb-2">
                  {cube.motherCubeName} ({cube.motherCubeNo})
                </p>
                <div className="space-y-1.5">
                  {cube.versions.map((ver) => {
                    const selected = isSelected(cube.id, ver.version);
                    return (
                      <button
                        key={ver.version}
                        type="button"
                        onClick={() => toggleVersion(cube.id, ver.version)}
                        className="flex items-center gap-2 w-full text-left group"
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selected ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'}`}>
                          {selected && <Check size={10} className="text-white" />}
                        </div>
                        <span className={`text-xs font-medium ${selected ? 'text-primary' : 'text-foreground'}`}>{ver.version}</span>
                        <span className="text-xs text-muted-foreground">({ver.date})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Summary counters */}
          <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2">
            {[
              { label: 'BOXES', value: fulfillment?.totalBoxes ?? 0, color: 'text-primary' },
              { label: 'KITS', value: fulfillment?.totalKits ?? 0, color: 'text-violet-600' },
              { label: 'ITEMS', value: fulfillment?.totalItems ?? 0, color: 'text-orange-500' },
            ].map((s) => (
              <div key={s.label} className="text-center bg-muted/40 rounded-lg py-2">
                <p className="text-2xs text-muted-foreground font-medium">{s.label}</p>
                <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            <RefreshCw size={14} />
            Reset
          </button>
          <button
            type="button"
            onClick={handleCreateOrder}
            disabled={!canSubmit}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ShoppingCart size={14} />
            Create Order
          </button>
        </div>

        {showSuccess && (
          <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
            <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0" />
            <span className="text-xs font-medium text-emerald-700">Order created successfully!</span>
          </div>
        )}
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        {/* Box Selection */}
        <div className="bg-card border border-border rounded-xl p-4 flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <Box size={16} className="text-primary" />
            <span className="font-semibold text-sm text-foreground">Box Selection</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {selectedBoxes.length} definition{selectedBoxes.length !== 1 ? 's' : ''} found from selected cubes
          </p>
          {selectedBoxes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Box size={40} className="text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">Select a Mother Cube version to see available boxes.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedBoxes.map((sb, idx) => {
                const key = `${sb.cubeId}-${sb.version}-${sb.box.boxCode}`;
                const isOpen = expandedBoxes.has(key);
                return (
                  <div key={key} className="border border-border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleBox(key)}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                    >
                      <Box size={14} className="text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground">{sb.box.boxName}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{sb.box.boxCode}</span>
                        <span className="ml-2 text-xs text-muted-foreground">· {sb.cubeName} {sb.version}</span>
                      </div>
                      <span className="text-xs text-muted-foreground mr-2">{sb.box.kits.length} kit{sb.box.kits.length !== 1 ? 's' : ''}</span>
                      <ChevronRight size={14} className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="px-4 py-3 space-y-2 border-t border-border">
                        {sb.box.kits.map((kit) => (
                          <div key={kit.kitCode} className="bg-background rounded-lg p-3 border border-border/50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-foreground">{kit.kitName}</span>
                              <span className="text-xs text-muted-foreground">Qty: {kit.qty}</span>
                            </div>
                            <div className="space-y-1">
                              {kit.components.map((comp) => {
                                const stock = ITEM_INVENTORY.find((i) => i.itemCode === comp.itemCode);
                                const available = stock?.availableQty ?? 0;
                                const needed = comp.requiredQtyPerKit * kit.qty;
                                const ok = available >= needed;
                                return (
                                  <div key={comp.itemCode} className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground truncate flex-1">{comp.itemName}</span>
                                    <span className={`ml-2 font-medium ${ok ? 'text-emerald-600' : 'text-red-500'}`}>
                                      {available}/{needed}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fulfillment Analysis */}
        {fulfillment && (
          <>
            {/* Fulfillment Summary */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-primary" />
                <span className="font-semibold text-sm text-foreground">Fulfillment Analysis</span>
                <span className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full ${fulfillment.canFulfill ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                  {fulfillment.canFulfill ? '✓ Can Fulfill' : '⚠ Partial Fulfillment'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium mb-0.5">Production Suggestion</p>
                  <p className="text-xl font-bold text-blue-700">{fulfillment.productionSuggestion}×</p>
                  <p className="text-xs text-blue-500">full order sets possible</p>
                </div>
                <div className={`rounded-lg p-3 border ${fulfillment.shortageItems.length === 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                  <p className={`text-xs font-medium mb-0.5 ${fulfillment.shortageItems.length === 0 ? 'text-emerald-600' : 'text-red-600'}`}>Shortage Items</p>
                  <p className={`text-xl font-bold ${fulfillment.shortageItems.length === 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fulfillment.shortageItems.length}</p>
                  <p className={`text-xs ${fulfillment.shortageItems.length === 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {fulfillment.shortageItems.length === 0 ? 'All items available' : 'items need procurement'}
                  </p>
                </div>
              </div>

              {/* Item Breakdown */}
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Item</th>
                      <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Required</th>
                      <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Available</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {fulfillment.itemBreakdown.map((row) => (
                      <tr key={row.itemCode} className={row.sufficient ? '' : 'bg-red-50/50'}>
                        <td className="px-3 py-2 text-foreground">{row.itemName}</td>
                        <td className="px-3 py-2 text-right text-foreground">{row.required}</td>
                        <td className="px-3 py-2 text-right text-foreground">{row.available}</td>
                        <td className="px-3 py-2 text-center">
                          {row.sufficient
                            ? <span className="inline-flex items-center gap-1 text-emerald-600"><Check size={11} />OK</span>
                            : <span className="inline-flex items-center gap-1 text-red-500"><AlertTriangle size={11} />Short</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Requisition Suggestions */}
            {fulfillment.requisitions.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb size={16} className="text-amber-500" />
                  <span className="font-semibold text-sm text-foreground">Suggested Requisitions</span>
                  <span className="text-xs text-muted-foreground ml-1">— auto-suggested based on order</span>
                </div>
                <div className="space-y-2">
                  {fulfillment.requisitions.map((req) => {
                    const pct = req.requiredQty > 0 ? Math.round((req.canMake / req.requiredQty) * 100) : 100;
                    return (
                      <div key={req.kitCode} className="border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-xs font-semibold text-foreground">{req.kitName}</span>
                            <span className="ml-2 text-xs text-muted-foreground">{req.kitCode}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs font-medium text-foreground">{req.canMake}/{req.requiredQty}</span>
                            {req.shortfall > 0 && (
                              <span className="ml-2 text-xs text-red-500">−{req.shortfall} short</span>
                            )}
                          </div>
                        </div>
                        <div className="w-full bg-muted rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : pct > 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Suggest requisition for <span className="font-semibold text-foreground">{req.requiredQty} kits</span>
                          {req.shortfall > 0 && <span className="text-amber-600"> · {req.shortfall} need procurement</span>}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shortage List */}
            {fulfillment.shortageItems.length > 0 && (
              <div className="bg-card border border-red-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-red-500" />
                    <span className="font-semibold text-sm text-foreground">Shortage List</span>
                    <span className="text-xs bg-red-100 text-red-600 border border-red-200 px-2 py-0.5 rounded-full font-medium">
                      {fulfillment.shortageItems.length} items
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadShortageCSV(fulfillment.shortageItems, 'DRAFT')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors"
                  >
                    <Download size={13} />
                    Download CSV
                  </button>
                </div>
                <div className="overflow-hidden rounded-lg border border-red-100">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-red-50">
                        <th className="text-left px-3 py-2 font-semibold text-red-700">Item Code</th>
                        <th className="text-left px-3 py-2 font-semibold text-red-700">Item Name</th>
                        <th className="text-right px-3 py-2 font-semibold text-red-700">Required</th>
                        <th className="text-right px-3 py-2 font-semibold text-red-700">Available</th>
                        <th className="text-right px-3 py-2 font-semibold text-red-700">Shortage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {fulfillment.shortageItems.map((item) => (
                        <tr key={item.itemCode} className="bg-white">
                          <td className="px-3 py-2 font-mono text-muted-foreground">{item.itemCode}</td>
                          <td className="px-3 py-2 text-foreground">{item.itemName}</td>
                          <td className="px-3 py-2 text-right text-foreground">{item.required}</td>
                          <td className="px-3 py-2 text-right text-amber-600">{item.available}</td>
                          <td className="px-3 py-2 text-right font-semibold text-red-600">{item.shortage}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {!fulfillment && (
          <div className="flex-1 bg-card border border-border rounded-xl flex flex-col items-center justify-center py-16 text-center">
            <ShoppingCart size={40} className="text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">Select Mother Cube versions to see fulfillment analysis</p>
            <p className="text-xs text-muted-foreground/70 mt-1">System will calculate production possibilities and shortage items</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Fulfillment Timeline Component ──────────────────────────────────────────
const STAGE_DEFS: { stage: FulfillmentStage; label: string; icon: React.ReactNode; desc: string }[] = [
  { stage: 'Order', label: 'Order', icon: <ClipboardList size={14} />, desc: 'Order received & confirmed' },
  { stage: 'Picking', label: 'Picking', icon: <Package size={14} />, desc: 'Items being picked from racks' },
  { stage: 'Dispatch', label: 'Dispatch', icon: <Truck size={14} />, desc: 'Packed & dispatched to customer' },
];

function FulfillmentTimeline({ order }: { order: OrderRecord }) {
  const stageOrder: FulfillmentStage[] = ['Order', 'Picking', 'Dispatch'];
  const currentIdx = stageOrder.indexOf(order.currentStage);

  return (
    <div className="px-4 py-4 bg-muted/20 border-t border-border">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Fulfillment Timeline</p>
      <div className="flex items-start gap-0">
        {STAGE_DEFS.map((def, idx) => {
          const stageData = order.stages.find((s) => s.stage === def.stage);
          const isCompleted = stageData?.completedAt != null;
          const isCurrent = order.currentStage === def.stage && !isCompleted;
          const isPending = idx > currentIdx;
          const isLast = idx === STAGE_DEFS.length - 1;

          return (
            <div key={def.stage} className="flex items-start flex-1">
              {/* Stage node */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                    isCompleted
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : isCurrent
                      ? 'bg-primary border-primary text-white animate-pulse' :'bg-muted border-border text-muted-foreground'
                  }`}
                >
                  {isCompleted ? <Check size={14} /> : def.icon}
                </div>
                {/* Stage label */}
                <div className="mt-2 text-center">
                  <p className={`text-xs font-semibold ${isCompleted ? 'text-emerald-600' : isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
                    {def.label}
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-0.5 max-w-[90px] text-center leading-tight">
                    {stageData?.completedAt
                      ? stageData.completedAt
                      : stageData?.note
                      ? <span className={isCurrent ? 'text-primary/80' : ''}>{stageData.note}</span>
                      : <span className="text-muted-foreground/50">{def.desc}</span>}
                  </p>
                </div>
              </div>
              {/* Connector line */}
              {!isLast && (
                <div className="flex-1 mt-4 mx-1">
                  <div className={`h-0.5 w-full rounded-full ${isCompleted && idx < currentIdx ? 'bg-emerald-400' : isCurrent ? 'bg-primary/30' : 'bg-border'}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Stage Badge ──────────────────────────────────────────────────────────────
function StageBadge({ stage }: { stage: FulfillmentStage }) {
  if (stage === 'Dispatch') return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <Truck size={10} />Dispatch
    </span>
  );
  if (stage === 'Picking') return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
      <Package size={10} />Picking
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
      <ClipboardList size={10} />Order
    </span>
  );
}

// ─── Order History Tab ────────────────────────────────────────────────────────
function OrderHistoryTab({ orders }: { orders: OrderRecord[] }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const PAGE_SIZE = 10;

  const filtered = orders.filter((o) => {
    const matchSearch =
      !search ||
      o.orderNo.toLowerCase().includes(search.toLowerCase()) ||
      o.customerName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const statusOptions: OrderStatus[] = ['Pending', 'In Production', 'Fulfilled', 'Partial', 'Cancelled'];

  const summaryCards = [
    { label: 'Total Orders', value: orders.length, color: 'text-primary', bg: 'bg-primary/5 border-primary/20' },
    { label: 'Fulfilled', value: orders.filter((o) => o.status === 'Fulfilled').length, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
    { label: 'In Production', value: orders.filter((o) => o.status === 'In Production').length, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
    { label: 'Pending', value: orders.filter((o) => o.status === 'Pending').length, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  ];

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        {summaryCards.map((c) => (
          <div key={c.label} className={`rounded-xl border p-4 ${c.bg}`}>
            <p className="text-xs text-muted-foreground font-medium">{c.label}</p>
            <p className={`text-2xl font-bold mt-1 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search orders or customers…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        >
          <option value="">All Statuses</option>
          {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} order{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground w-6"></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Order No.</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Delivery Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Mother Cubes</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Boxes</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Kits</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Fulfillment</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Stage</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-10 text-center text-sm text-muted-foreground">No orders found</td>
              </tr>
            ) : (
              paginated.map((order) => (
                <React.Fragment key={order.id}>
                  <tr
                    className="hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  >
                    <td className="px-4 py-3 text-center">
                      <ChevronRight
                        size={14}
                        className={`text-muted-foreground transition-transform ${expandedOrder === order.id ? 'rotate-90' : ''}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-primary font-semibold">{order.orderNo}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{order.customerName}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{order.deliveryDate}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {order.motherCubes.map((mc) => (
                          <span key={mc} className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">{mc}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground font-medium">{order.totalBoxes}</td>
                    <td className="px-4 py-3 text-right text-foreground font-medium">{order.totalKits}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 bg-muted rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${order.fulfillmentPct === 100 ? 'bg-emerald-500' : order.fulfillmentPct > 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${order.fulfillmentPct}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{order.fulfillmentPct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StageBadge stage={order.currentStage} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${orderStatusColor(order.status)}`}>
                        {orderStatusIcon(order.status)}
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-muted-foreground">{order.createdDate}</td>
                  </tr>
                  {expandedOrder === order.id && (
                    <tr>
                      <td colSpan={11} className="p-0">
                        <FulfillmentTimeline order={order} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors disabled:opacity-40"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${p === page ? 'bg-primary text-white' : 'border border-border bg-card hover:bg-muted text-foreground'}`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors disabled:opacity-40"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function OrderManagementPage() {
  const [activeTab, setActiveTab] = useState<'place' | 'history'>('place');
  const [orders, setOrders] = useState<OrderRecord[]>(INITIAL_ORDERS);

  const handleOrderCreated = (order: OrderRecord) => {
    setOrders((prev) => [order, ...prev]);
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-screen bg-background">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <ShoppingCart size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">Order Management</h1>
              <p className="text-xs text-muted-foreground">Create and track complex orders</p>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex items-center bg-muted/50 rounded-lg p-1 gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('place')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'place' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <ShoppingCart size={14} />
              Place Order
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'history' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <History size={14} />
              Order History
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 overflow-auto">
          {activeTab === 'place' ? (
            <PlaceOrderTab onOrderCreated={handleOrderCreated} />
          ) : (
            <OrderHistoryTab orders={orders} />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
